import { FullProduct } from '../../../models/Product';
import { UserGroup } from '../../../models/UserGroup';

const normalizeColor = (color?: string | null) => color?.trim() ?? '';

export const extractProductColors = (product: FullProduct): string[] =>
  Array.from(
    new Set([
      ...(product.LogoInfo ?? []).map((logo) => logo?.color),
      ...(product.MediaContent ?? []).map((media) => media?.color),
      ...(product.WebstoreProductPartArray ?? []).map((part) => part?.ColorArray?.Color?.colorName),
      ...(product.ProductPartArray?.ProductPart ?? []).map(
        (part) => part?.ColorArray?.Color?.colorName,
      ),
      ...(product.minPriceData?.colors ?? []).map((color) => color?.colorName),
    ]),
  )
    .map(normalizeColor)
    .filter(Boolean);

const filterAllowedColors = <T>(
  allowedColors: Set<string>,
  arr: T[],
  getColor: (item: T) => string,
) => arr?.filter((item) => allowedColors.has(normalizeColor(getColor(item)))) || [];

const filterColors = (product: FullProduct, allowedColors: Set<string>) => {
  return {
    ...product,
    LogoInfo: filterAllowedColors(allowedColors, product.LogoInfo ?? [], (logo) => logo?.color),
    MediaContent: filterAllowedColors(
      allowedColors,
      product.MediaContent ?? [],
      (media) => media?.color,
    ),
    WebstoreProductPartArray: filterAllowedColors(
      allowedColors,
      product.WebstoreProductPartArray ?? [],
      (part) => part?.ColorArray?.Color?.colorName,
    ),
    ProductPartArray: {
      ProductPart: filterAllowedColors(
        allowedColors,
        product.ProductPartArray?.ProductPart ?? [],
        (part) => part?.ColorArray?.Color?.colorName,
      ),
    },
    minPriceData: {
      ...product.minPriceData,
      colors: filterAllowedColors(
        allowedColors,
        product.minPriceData?.colors ?? [],
        (color) => color?.colorName,
      ),
    },
  };
};

export function filterProductAllowedColors(product: FullProduct, userGroups: UserGroup[]) {
  if (!product) return null;

  const userGroupsIds = userGroups.map((group) => group._id);

  const productLevelRestrictions = product?.userGroupRestrictions?.filter(
    (group) => userGroupsIds.includes(group?.groupId) && group?.isColorRestricted,
  );

  if (productLevelRestrictions?.length > 0) {
    const productAllowedColors = new Set(
      productLevelRestrictions?.flatMap((restriction) =>
        restriction?.colors?.map((color) => color?.trim()),
      ),
    );

    if (productAllowedColors.size > 0) {
      return filterColors(product, productAllowedColors);
    }
    return product;
  }

  const userGroupRestrictions = userGroups?.filter(
    (group) => group?.productRestrictions?.isColorRestricted,
  );

  if (userGroupRestrictions?.length > 0) {
    const userGroupAllowedColors = new Set(
      userGroupRestrictions.flatMap((group) =>
        group?.productRestrictions?.colors?.map((color) => color.trim()),
      ),
    );

    if (userGroupAllowedColors.size > 0) {
      const productColors = extractProductColors(product);
      const hasMatch = productColors.some((color) => userGroupAllowedColors.has(color));

      if (hasMatch) {
        return filterColors(product, userGroupAllowedColors);
      }
      return product;
    }
  }

  return product;
}
